<template>
    <div class="base-pagination">
        <BaseButton :disabled="isPreviousButtonDisabled" @click.enter="previousPage">
            ←
        </BaseButton>

        <template v-for="paginationTrigger in paginationTriggers" :key="paginationTrigger">
            <!-- Mostrar puntos suspensivos como texto plano -->
            <span
                v-if="paginationTrigger === '...'"
                class="base-pagination__ellipsis"
            >
                ...
            </span>

            <!-- Mostrar botones de página solo para números -->
            <BasePaginationTrigger
                v-else
                :class="{
                    'base-pagination__description--current': paginationTrigger === currentPage,
                }"
                :pageNumber="paginationTrigger"
                class="base-pagination__description"
                @loadPage="onLoadPage"
            />
        </template>

        <BaseButton :disabled="isNextButtonDisabled" @click.enter="nextPage">
            →
        </BaseButton>
    </div>
</template>

<script>
import { computed } from "vue";
import BaseButton from "./BaseButton.vue";
import BasePaginationTrigger from "./BasePaginationTrigger.vue";

export default {
    name: "BasePagination",
    components: {
        BaseButton,
        BasePaginationTrigger,
    },
    props: {
        currentPage: {
            type: Number,
            required: true,
        },
        pageCount: {
            type: Number,
            required: true,
        },
        visiblePagesCount: {
            type: Number,
            default: 5,
            validator: value => value >= 3, // mínimo 3 páginas visibles
        },
    },
    emits: ['next-page', 'previous-page', 'load-page'],
    setup(props, { emit }) {
        const isPreviousButtonDisabled = computed(() => props.currentPage <= 1);
        const isNextButtonDisabled = computed(() => props.currentPage >= props.pageCount);

        const paginationTriggers = computed(() => {
            const { currentPage, pageCount, visiblePagesCount } = props;

            // Validar que los props sean números válidos
            if (!currentPage || !pageCount || pageCount <= 0) {
                return [1];
            }

            const half = Math.floor(visiblePagesCount / 2);

            let start = Math.max(2, currentPage - half);
            let end = Math.min(pageCount - 1, currentPage + half);

            // Ajusta el rango si está cerca de los extremos
            if (currentPage <= half) {
                start = 2;
                end = Math.min(pageCount - 1, visiblePagesCount);
            } else if (currentPage > pageCount - half) {
                start = Math.max(2, pageCount - visiblePagesCount + 1);
                end = pageCount - 1;
            }

            const pages = [];

            if (pageCount <= visiblePagesCount + 2) {
                // Mostrar todas las páginas si son pocas
                for (let i = 1; i <= pageCount; i++) {
                    pages.push(i);
                }
            } else {
                // Siempre mostrar la primera página
                pages.push(1);

                // Agregar puntos suspensivos si hay un gap
                if (start > 2) {
                    pages.push('...');
                }

                // Agregar páginas del rango medio
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }

                // Agregar puntos suspensivos si hay un gap al final
                if (end < pageCount - 1) {
                    pages.push('...');
                }

                // Siempre mostrar la última página (si es mayor que 1)
                if (pageCount > 1) {
                    pages.push(pageCount);
                }
            }

            return pages;
        });

        const nextPage = () => {
            if (!isNextButtonDisabled.value) {
                emit("next-page");
            }
        };

        const previousPage = () => {
            if (!isPreviousButtonDisabled.value) {
                emit("previous-page");
            }
        };

        const onLoadPage = (pageNumber) => {
            // Validar que el número de página sea válido
            const page = parseInt(pageNumber);
            if (!isNaN(page) && page >= 1 && page <= props.pageCount && page !== props.currentPage) {
                emit("load-page", page);
            }
        };

        return {
            isPreviousButtonDisabled,
            isNextButtonDisabled,
            paginationTriggers,
            nextPage,
            previousPage,
            onLoadPage,
        };
    },
};
</script>

<style lang="scss" scoped>
$c-primary-accent: #2563EB;
$c-gray-light: #6c757d;
$c-gray-border: #dee2e6;

.base-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin: 1rem 0;

    &__description {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 0.25rem;
        font-size: 16px;
        font-weight: 600;
        min-width: 40px;
        height: 40px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        border: 1px solid $c-gray-border;
        background-color: #fff;
        color: #495057;

        &:hover {
            background-color: #f8f9fa;
            border-color: $c-primary-accent;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        &--current {
            color: #fff;
            background-color: $c-primary-accent;
            border-color: $c-primary-accent;
            font-weight: 700;
            box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);

            &:hover {
                background-color: darken($c-primary-accent, 10%);
                border-color: darken($c-primary-accent, 10%);
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
            }
        }
    }

    &__ellipsis {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 0.25rem;
        font-size: 16px;
        font-weight: 600;
        color: $c-gray-light;
        user-select: none;
        min-width: 40px;
        height: 40px;
        cursor: default;
    }

    .arrow-left {
        transform: rotateZ(180deg);
    }
}

// Responsive design
@media (max-width: 768px) {
    .base-pagination {
        gap: 0.25rem;

        &__description {
            font-size: 14px;
            min-width: 35px;
            height: 35px;
            margin: 0 0.125rem;
        }

        &__ellipsis {
            font-size: 14px;
            min-width: 35px;
            height: 35px;
            margin: 0 0.125rem;
        }
    }
}

@media (max-width: 576px) {
    .base-pagination {
        gap: 0.125rem;

        &__description {
            font-size: 12px;
            min-width: 30px;
            height: 30px;
            margin: 0;
        }

        &__ellipsis {
            font-size: 12px;
            min-width: 30px;
            height: 30px;
            margin: 0;
        }
    }
}

// Mejoras de accesibilidad
.base-pagination__description:focus {
    outline: 2px solid $c-primary-accent;
    outline-offset: 2px;
}

// Animaciones
@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}

</style>
